import React from "react";
import { FileDown, Loader } from "lucide-react";
import { useConsumerHouseReconciliationItems } from "../hooks/useTreasury";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { downloadCsv } from "../utils/exportCsv";
import { ITEM_KINDS } from "../utils/reconciliationKinds";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-");

// The individual records behind one cause, shown inline under it. Only
// fetched while it is on screen.
function ReconciliationRecords({ kind, currency, since }) {
  const { data, isLoading, isError, error } = useConsumerHouseReconciliationItems({ kind, currency, since, enabled: true });
  const meta = ITEM_KINDS[kind];

  if (isLoading) {
    return (
      <p className="flex gap-2 items-center py-3 text-sm text-gray-500">
        <Loader className="w-4 h-4 animate-spin" /> Loading records...
      </p>
    );
  }
  if (isError) return <p className="py-3 text-sm text-red-600">Could not load the records: {error?.message}</p>;
  if (data.items.length === 0) return <p className="py-3 text-sm text-gray-500">No records match.</p>;

  const handleExport = () =>
    downloadCsv(
      `gap-${kind}-${currency}-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Date", "Reference", "Customer", "Email", `Amount (${currency})`, "Status", "Detail"],
      data.items.map((i) => [i.date, i.reference ?? "", i.customer?.name ?? "", i.customer?.email ?? "", i.amount, i.status ?? "", i.detail]),
    );

  return (
    <div className="py-3">
      {meta?.note && <p className="mb-2 max-w-3xl text-xs text-gray-500">{meta.note}</p>}
      <div className="flex justify-between items-center mb-2">
        <p className="text-xs text-gray-500">
          {data.total > data.shown
            ? `Showing the newest ${data.shown} of ${data.total}. The export has the same ${data.shown}.`
            : `${data.total} record${data.total === 1 ? "" : "s"}.`}
        </p>
        <button
          onClick={handleExport}
          className="flex gap-2 items-center px-3 py-1.5 text-xs font-bold text-[#013941] bg-white rounded-3xl border border-[#013941] hover:bg-gray-50"
        >
          <FileDown className="w-4 h-4" />
          Export CSV
        </button>
      </div>
      <div className="overflow-auto max-h-80 bg-white rounded border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="sticky top-0 text-xs tracking-wider text-left text-gray-500 uppercase bg-gray-50">
              <th className="px-3 py-2 font-semibold">When</th>
              <th className="px-3 py-2 font-semibold">Reference</th>
              <th className="px-3 py-2 font-semibold">Customer</th>
              <th className="px-3 py-2 font-semibold text-right">Amount</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map((i, n) => (
              <tr key={`${i.reference}-${n}`}>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{when(i.date)}</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-700 break-all">{i.reference ?? "-"}</td>
                <td className="px-3 py-2 text-gray-700">
                  {i.customer ? (
                    <>
                      <span className="block">{i.customer.name || "-"}</span>
                      <span className="block text-xs text-gray-500 break-all">{i.customer.email}</span>
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${i.amount < 0 ? "text-red-600" : "text-gray-900"}`}>
                  {formatCurrencyAmount(i.amount, currency)}
                </td>
                <td className="px-3 py-2 text-gray-700">{i.status ?? "-"}</td>
                <td className="px-3 py-2 text-gray-600">{i.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReconciliationRecords;
